// src/axiosInstance.js

import axios from 'axios';
import axiosRetry from 'axios-retry';

// Determine the appropriate base URL
const getBaseURL = () => {
  // If we're accessing via HTTPS (intercollect.app) or any HTTPS site
  if (window.location.hostname.includes('intercollect.app') || window.location.protocol === 'https:') {
    console.log('Using relative URL for HTTPS proxy access');
    return ''; // Remove '/api' to prevent double prefix
  }
  
  // Get the current hostname and port
  const hostname = window.location.hostname;
  const port = window.location.port;
  
  // Check if we're running on localhost — map frontend port to backend port
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const backendPort = port === '3001' ? '5002' : '5000'; // dev: 3001→5002, prod: 3000→5000
    return `http://localhost:${backendPort}`;
  }
  
  // If we're accessing via IP address directly (likely on local network)
  // Use the same hostname but with the backend port
  if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    // When accessing via IP, use port 5000 for the API
    return `http://${hostname}:5000`; // Remove '/api' to prevent double prefix
  }
  
  // Default fallback - use a simple relative path
  return '';
};

// Create an Axios instance
const axiosInstance = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for CORS
});

// Add a method to get the base API URL
// This is useful for constructing image URLs
axiosInstance.getBaseServerUrl = function() {
  return this.defaults.baseURL || '';
};

// Add a helper method to construct image URLs
axiosInstance.getImageUrl = function(imagePath) {
  console.log('getImageUrl called with:', imagePath);
  
  if (!imagePath) {
    console.log('No image path provided');
    return '';
  }
  
  // Handle protocol-relative URLs (starting with //)
  if (imagePath.startsWith('//')) {
    // Convert to https:// or http:// based on current protocol
    const protocol = window.location.protocol;
    const absoluteUrl = `${protocol}${imagePath}`;
    console.log('Protocol-relative URL converted to:', absoluteUrl);
    
    // Check if it's an external image that needs proxying
    const externalDomains = [
      'images.igdb.com',
      'media.rawg.io',
      'rebrickable.com',
      'img.bricklink.com',
      'brickset.com'
    ];
    
    // Check if the URL is from one of our external domains
    const isExternalImage = externalDomains.some(domain => absoluteUrl.includes(domain));
    
    if (isExternalImage) {
      // Use our proxy endpoint
      const baseApiUrl = this.defaults.baseURL || '';
      const encodedUrl = encodeURIComponent(absoluteUrl);
      const proxyUrl = `${baseApiUrl}/image-proxy?url=${encodedUrl}`;
      console.log('Using proxy for external image:', proxyUrl);
      return proxyUrl;
    }
    
    console.log('Returning absolute URL:', absoluteUrl);
    return absoluteUrl;
  }
  
  // If it's already a full URL
  if (imagePath.startsWith('http')) {
    console.log('URL already starts with http:', imagePath);
    
    // Check if it's an external image that needs proxying
    const externalDomains = [
      'images.igdb.com',
      'media.rawg.io',
      'rebrickable.com',
      'img.bricklink.com',
      'brickset.com'
    ];
    
    // Check if the URL is from one of our external domains
    const isExternalImage = externalDomains.some(domain => imagePath.includes(domain));
    
    if (isExternalImage) {
      // Use our proxy endpoint
      const baseApiUrl = this.defaults.baseURL || '';
      const encodedUrl = encodeURIComponent(imagePath);
      const proxyUrl = `${baseApiUrl}/image-proxy?url=${encodedUrl}`;
      console.log('Using proxy for external image with http:', proxyUrl);
      return proxyUrl;
    }
    
    // If not an external domain that needs proxying, return as is
    console.log('Returning full URL as is:', imagePath);
    return imagePath;
  }
  
  // Make sure the path starts with a slash
  const normalizedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
  
  // Construct the full URL
  let baseServerUrl = this.getBaseServerUrl();
  
  // When accessed via HTTPS, use relative URLs for images to prevent Mixed Content issues
  if (window.location.protocol === 'https:') {
    // If baseServerUrl contains the internal Docker hostname, replace it with a relative URL
    if (baseServerUrl.includes('intercollect-backend:5000')) {
      console.log('Fixing hardcoded backend URL in image path');
      baseServerUrl = '';
    }
  }
  
  // Remove any trailing slash from baseServerUrl to avoid double slashes
  const cleanBaseUrl = baseServerUrl.endsWith('/') ? baseServerUrl.slice(0, -1) : baseServerUrl;
  const fullUrl = `${cleanBaseUrl}${normalizedPath}`;
  
  console.log('Constructed image URL:', fullUrl);
  return fullUrl;
};

// Configure retry behavior
axiosRetry(axiosInstance, { 
  retries: 3,
  retryDelay: (retryCount) => {
    return retryCount * 1000; // Wait 1s, 2s, 3s between retries
  },
  retryCondition: (error) => {
    // Retry on network errors or rate limit errors (429)
    return axiosRetry.isNetworkOrIdempotentRequestError(error) || 
           error.response?.status === 429;
  }
});

// Track concurrent requests
const pendingRequests = new Map();

// Add a request interceptor to attach the token to headers and manage request concurrency
axiosInstance.interceptors.request.use(
  async (config) => {
    // Fix for Mixed Content issues when accessed via HTTPS
    if (window.location.protocol === 'https:') {
      // Force the baseURL to be a relative URL
      config.baseURL = '/api';
      
      // If the URL contains the internal Docker hostname, replace it
      if (config.url && config.url.includes('intercollect-backend:5000')) {
        console.log('Fixing hardcoded backend URL:', config.url);
        config.url = config.url.replace(/http:\/\/intercollect-backend:5000\/api\/?/, '');
      }
    }
    
    const requestKey = `${config.method}-${config.url}`;

    // Wait for any pending identical request to complete.
    // Skip if this is an axiosRetry retry — the original request's pending slot was never
    // cleaned up (resolvePending runs in the response interceptor, which axiosRetry bypasses
    // when it intercepts the error to retry), so waiting would deadlock forever.
    const isRetry = !!(config['axios-retry']?.retryCount);
    if (!isRetry && pendingRequests.has(requestKey)) {
      await pendingRequests.get(requestKey);
    }

    // Create a new promise for this request
    let resolvePending;
    const pendingPromise = new Promise(resolve => {
      resolvePending = resolve;
    });
    pendingRequests.set(requestKey, pendingPromise);

    // Store the resolve function to be called when the request completes
    config.resolvePending = () => {
      resolvePending();
      pendingRequests.delete(requestKey);
    };

    // Add cache-busting for search endpoints
    if (config.url && config.url.includes('/search')) {
      // Initialize params object if it doesn't exist
      config.params = config.params || {};
      // Add timestamp to prevent caching
      config.params._t = Date.now();
      // Add cache-busting headers
      config.headers = {
        ...config.headers,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      };
    }

    console.log('API Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      data: config.data,
      headers: config.headers
    });

    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('No token found in localStorage');
    }
    return config;
  },
  (error) => {
    console.error('Request Error:', error);
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle responses and errors
axiosInstance.interceptors.response.use(
  (response) => {
    // Resolve the pending promise for this request
    if (response.config.resolvePending) {
      response.config.resolvePending();
    }

    console.log('API Response:', {
      status: response.status,
      statusText: response.statusText,
      url: response.config.url,
      data: response.data
    });
    return response;
  },
  async (error) => {
    // Resolve the pending promise even if there's an error
    if (error.config?.resolvePending) {
      error.config.resolvePending();
    }

    console.error('Response Error:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      data: error.response?.data,
      error: error.message
    });

    // Handle token expiration or invalid token
    if (error.response?.status === 401 || error.response?.status === 403) {
      const errMsg = String(error.response?.data?.error || '').toLowerCase();
      const isInvalidOrExpiredToken =
        errMsg.includes('invalid token') ||
        errMsg.includes('token has expired') ||
        errMsg.includes('token expired') ||
        errMsg.includes('token verification failed') ||
        errMsg.includes('invalid token payload');

      if (isInvalidOrExpiredToken) {
        console.log('Invalid/expired token detected, clearing token');
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }

    // Handle rate limit errors — only retry once for non-auth endpoints
    // Auth endpoints (login, register) should surface 429 immediately
    if (error.response?.status === 429 && error.config && !error.config._429retried) {
      const url = error.config.url || '';
      const isAuthEndpoint = url.includes('/auth/') || url.includes('/mfa/');
      if (!isAuthEndpoint) {
        error.config._429retried = true;
        const retryAfter = parseInt(error.response.headers['retry-after']) || 5;
        await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
        return axiosInstance(error.config);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
